/**
 * Country gating registry — single source of truth for "this thing only
 * exists in these countries." Three kinds of entry:
 *
 *   - `page:<slug>`       — whole route SSG'd only in listed countries.
 *                           Sitemap, hreflang, locale picker, search index
 *                           all filter on this.
 *   - `section:<page>.<slot>` — UI block inside a shared page.
 *   - `feature:<name>`     — behaviour gate that has no visible section
 *                           (provider mount, side-effect hook).
 *
 * Why a registry instead of inline `country === "xx"` checks:
 *   - Greppable: `lib/country-gates.ts` is the audit of what varies.
 *   - Lintable: gate IDs in code must match a key here.
 *   - Discoverable: agents and humans have one place to look + one skill
 *     to read (`.claude/skills/country-gating/SKILL.md`).
 *
 * NOT here: PostHog flags (per-user A/B — `lib/experiments.ts`),
 * release-time kill switches (`lib/release-flags.ts`), or whole-country
 * enablement (the `*_STATUS` vars, also read in `lib/release-flags.ts`).
 * Those are orthogonal.
 *
 * Adding a new gate: see `.claude/skills/country-gating/SKILL.md`.
 */

import type { Country } from "@/i18n/config"
import { canonicalSlugPath } from "@/lib/localized-route-slugs"

type CountryGate = {
  /** Country codes where this thing renders / exists. Non-empty. */
  countries: readonly Country[]
  /** One-line note: what this gates and why. Read by humans + agents. */
  description: string
  /**
   * i18n key prefixes whose copy is *exclusive* to this gated surface. The
   * translation-review queue uses these to drop strings a reviewer's country can
   * never see in context (`isKeyCountryGatedOut`). Page gates are handled
   * separately, by render path — only set this for `section:`/`feature:` gates,
   * and only when the prefix is owned solely by gated content (nothing un-gated
   * renders it). A wrong entry hides real review work, so err towards omitting.
   */
  reviewKeyPrefixes?: readonly string[]
}

export const COUNTRY_GATES = {
  // --- Pages: route only SSG'd in listed countries ---
  "page:insurance": {
    countries: ["ch"],
    description:
      "Insurance coverage checker — Swiss-reimbursement market only.",
  },
  "page:membership": {
    countries: ["ch"],
    description:
      "Ahead membership page. CH sells it. DE dropped it 2026-09-28 " +
      "(AHD-13290): Germany sells the blood check-up only, so the page, its " +
      "sitemap entry and every link into it went, and /de/de/membership now " +
      "308s to the Vorsorge-Starter (next.config.ts). Waitlist NL doesn't offer membership " +
      "at all (2026-07-29 decision, AHD-9361) — no page, no promise. AT was " +
      "dropped 2026-08-28 when the Austrian launch set was trimmed to a " +
      "13-page waitlist site; it also left feature:membership-first-positioning " +
      "in the same pass, so nothing on AT points here any more.",
  },
  "page:insurance-checker-terms": {
    countries: ["ch"],
    description: "T&Cs for the CH-only insurance checker.",
  },
  "page:full-body-mri-zurich": {
    countries: ["ch"],
    description: "Zurich-specific MRI landing page.",
  },
  "page:go/bloodtest-munich": {
    countries: ["de"],
    description:
      "Munich blood-test ad landing page — DE-only (single German clinic, " +
      "München Marienplatz). Registered so switching country from the " +
      "picker falls back to the target country's home instead of a 404. The " +
      "page also self-gates with a client-side notFound() for direct hits to " +
      "another country (single-clinic ad LP, geography not market status).",
  },
  "page:go/bloodtest-berlin": {
    countries: ["de"],
    description:
      "Berlin blood-test ad landing page — DE-only. Registered so switching " +
      "country from the picker falls back to the target country's home " +
      "instead of a 404. The page also self-gates with a client-side " +
      "notFound() for direct hits to another country (single-city ad LP, " +
      "geography not market status). No active partner clinic in Berlin yet, " +
      "so the page renders without its location strip until one lands in " +
      "data/bloodtest-de-cities.ts and the CMS.",
  },
  "page:go/bloodtest-frankfurt": {
    countries: ["de"],
    description:
      "Frankfurt blood-test ad landing page — DE-only. Registered so switching " +
      "country from the picker falls back to the target country's home " +
      "instead of a 404. The page also self-gates with a client-side " +
      "notFound() for direct hits to another country (single-city ad LP, " +
      "geography not market status). No active partner clinic in Frankfurt yet, " +
      "so the page renders without its location strip until one lands in " +
      "data/bloodtest-de-cities.ts and the CMS.",
  },
  "page:go/bloodtest-hamburg": {
    countries: ["de"],
    description:
      "Hamburg blood-test ad landing page — DE-only. Registered so switching " +
      "country from the picker falls back to the target country's home " +
      "instead of a 404. The page also self-gates with a client-side " +
      "notFound() for direct hits to another country (single-city ad LP, " +
      "geography not market status). No active partner clinic in Hamburg yet, " +
      "so the page renders without its location strip until one lands in " +
      "data/bloodtest-de-cities.ts and the CMS.",
  },
  "page:go/bloodtest-zurich": {
    countries: ["ch"],
    description:
      "Zurich blood-test ad landing page — CH-only (single Swiss clinic). " +
      "Registered so switching country from the picker falls back to the " +
      "target country's home instead of a 404. The page also self-gates with " +
      "a client-side notFound() for direct hits to another country " +
      "(single-clinic ad LP, geography not market status).",
  },
  "page:go/insurancemri": {
    countries: ["ch"],
    description:
      "MRI insurance-coverage ad landing page — CH-only, like /insurance: " +
      "the checker and the insurer examples only make sense where supplementary " +
      "insurers reimburse. DE dropped it 2026-09-28 (INS-106): Germany has no " +
      "insurance pages. Registered so switching country from the picker falls " +
      "back to the target country's home instead of a 404.",
  },
  "page:go/insuranceblood": {
    countries: ["ch"],
    description:
      "Blood-test insurance-coverage ad landing page — CH-only, same reasons " +
      "as page:go/insurancemri. DE dropped it 2026-09-28 (INS-106).",
  },
  "page:go/membership": {
    countries: ["ch"],
    description:
      "Membership ad landing page — CH-only, like page:membership. Austria " +
      "went first (AHD-13990); Germany and the Netherlands followed, since " +
      "neither sells or markets membership. The page 404s outside CH and " +
      "the picker falls back to the target country's home page.",
  },
  "page:go/female-hormones": {
    countries: ["ch", "nl", "de"],
    description:
      "/go/female-hormones ad landing page (hormone & fertility blood test). Sells the blood test " +
      "and its add-ons, which Austria doesn't offer (MRI Advanced and Pro " +
      "only), so it 404s there (AHD-14098).",
  },
  "page:go/heart-health": {
    countries: ["ch", "nl", "de"],
    description:
      "/go/heart-health ad landing page (cardiac blood test). Sells the blood test " +
      "and its add-ons, which Austria doesn't offer (MRI Advanced and Pro " +
      "only), so it 404s there (AHD-14098).",
  },
  "page:go/metabolism": {
    countries: ["ch", "nl", "de"],
    description:
      "/go/metabolism ad landing page (metabolic blood analysis). Sells the blood test " +
      "and its add-ons, which Austria doesn't offer (MRI Advanced and Pro " +
      "only), so it 404s there (AHD-14098).",
  },
  "page:go/immunity": {
    countries: ["ch", "nl", "de"],
    description:
      "/go/immunity ad landing page (immune blood markers). Sells the blood test " +
      "and its add-ons, which Austria doesn't offer (MRI Advanced and Pro " +
      "only), so it 404s there (AHD-14098).",
  },
  "page:go/lauraphilipps": {
    countries: ["ch", "de"],
    description:
      "Laura Philipp athlete campaign landing page — CH and DE. Germany " +
      "German was translated and human-reviewed (AHD-12258), so the copy is " +
      "no longer Swiss on its face and the `go.athlete*` keys are held to " +
      "the mirror-locale parity bar again. Germany sells the blood test but " +
      "not the full-body scan, so the page resolves its imaging gate from " +
      "the CMS products control plane and renders the blood-only shape " +
      "there: every surface that talks about the scan drops out and the CTAs " +
      "book the blood test. The Netherlands is deliberately absent — its " +
      "copy is ready (AHD-12260) but the market is waitlist-only, so every " +
      "CTA would open the waitlist modal whatever the page asks for. " +
      "Registered so switching country from the picker falls back to the " +
      "target country's home instead of a 404; the page also self-gates " +
      "with a client-side notFound() for direct hits from another country.",
  },
  "page:quiz": {
    countries: ["ch", "nl", "de"],
    description:
      'Quiz recommendation funnel (embedded + focused). CH + NL + DE. The funnel is market-aware (lib/quiz/market.ts): it resolves what each market can actually sell via resolveServiceAvailability, so on NL the MRI question routes to the waitlist (coming-soon at checkup status), the imaging follow-up step drops (those four services are not_available), and the insurer step + coverage band + pro-forma don\'t render (no Dutch insurance story). DE is German-only (/de/de) and blood-only: every imaging product is `availability: { "de-de": false }` in data/tests.ts, which the quiz reads as not_available, so both MRI steps drop and the result is always the Vorsorge-Starter; no insurer step (pure self-pay). AT still excluded: it needs the de-AT quiz namespace first.',
    // Deliberately on a `page:` gate, against the general guidance above. The
    // `quiz.` namespace is owned *solely* by this funnel — which is the
    // condition that actually matters — and this gate's country list is the
    // only accurate answer to "can a reviewer in country X see these strings".
    // It used to live on section:marketing.quiz-cta-banner (CH-only); leaving it
    // there would hide the Dutch strings from the NL reviewer, and dropping it
    // entirely would push 182 phantom strings into the DE/AT queues for a
    // funnel those markets don't serve.
    reviewKeyPrefixes: ["quiz."],
  },
  "page:careers/prevention-specialist-gp-landing": {
    countries: ["ch"],
    description:
      "Recruitment LP for French-speaking GPs in Suisse romande — CH-only (Swiss clinical role).",
  },
  "page:free-call": {
    countries: ["ch", "de"],
    description:
      "Discovery-call booking page. Hidden on NL during the waitlist phase (AHD-6839).",
  },
  "page:insights": {
    countries: ["ch", "nl", "at"],
    description:
      "Blog/insights listing and detail pages. NL shows the reviewed subset " +
      "only — per-post hiding via hideInCountries in the CMS (AHD-6892). DE " +
      "temporarily removed pending DE-market-appropriate translated content " +
      "(Swiss-focused articles were leaking to /de/de/ via SEO). AT opened " +
      "2026-09-28 so articles can be released one at a time from the CMS: " +
      "`de-AT` is in FALLBACK_SUPPRESSED_CMS_LOCALES, so an article with no " +
      "Austrian translation comes back EMPTY rather than Swiss — the leak " +
      "that got DE pulled cannot happen here. The listing therefore grows as " +
      "posts are localized (see the ahead-localize-at skill), and shows " +
      "nothing until the first one lands.",
  },
  "page:compare-health-check-ups-switzerland": {
    countries: ["ch"],
    description:
      "Compare preventive health check-ups page — CH-only (Swiss positioning; NL hidden per AHD-6919).",
  },
  // --- Health-concern organic pages ---
  // Standalone, indexable versions of the bespoke `/go/<concern>` pages,
  // living at clean top-level URLs (`/heart-health`, …) out of the /go ad
  // namespace. The /go originals stay as ad LPs but canonicalise here, so
  // Google indexes the organic URL and the ad campaigns keep a clean landing
  // page (no duplicate-content competition). CH-only for now: NL and DE keep
  // the /go ad LPs (self-canonical there) until the organic pages are opened
  // to those markets — add the country here and the link, sitemap entry,
  // hreflang and /go canonical all follow.
  "page:heart-health": {
    countries: ["ch"],
    description:
      "Heart-health organic landing page — the indexable sibling of the " +
      "/go/heart-health ad LP (cardiac blood test: ApoB, Lp(a), hs-CRP). " +
      "CH-only for now (NL/DE gated until launch there).",
  },
  "page:metabolism": {
    countries: ["ch"],
    description:
      "Metabolism organic landing page — the indexable sibling of the " +
      "/go/metabolism ad LP (metabolic blood analysis: blood sugar, HbA1c, " +
      "insulin/HOMA). CH-only for now (NL/DE gated until launch there).",
  },
  "page:female-hormones": {
    countries: ["ch"],
    description:
      "Female-hormones organic landing page — the indexable sibling of the " +
      "/go/female-hormones ad LP (hormone & fertility blood test: PCOS, " +
      "estradiol, LH/FSH, AMH). CH-only for now (NL/DE gated until launch " +
      "there).",
  },
  "page:tests/cortisol-blood-test": {
    countries: ["ch", "nl"],
    description:
      "Cortisol single-biomarker add-on landing page. CH-only originally " +
      "(AHD-3795); opened to NL on 2026-09-28 alongside its EUR 29 NL price, " +
      "since the two original reasons no longer hold — the copy is fully " +
      "translated and the price line hides itself on a market with no " +
      "authoritative EUR figure rather than showing a CHF one. AT was opened " +
      "on 2026-08-31 and closed again on 2026-09-28: Austria launches with " +
      "the three MRI packages only and sells no standalone add-on, so the " +
      "page had nothing to sell. DE has never asked for it.",
  },
  "page:tests/psa-blood-test": {
    countries: ["ch"],
    description:
      "PSA single-biomarker add-on landing page — CH-only. It's an add-on to " +
      "the Swiss Advanced blood test; EUR markets (DE/NL/AT) have no " +
      "authoritative price (Stripe is CHF-only) and the copy is CH-first " +
      "(AHD-3795).",
  },
  "page:membership-terms": {
    countries: ["ch"],
    description:
      "Membership T&Cs — CH only. Terms for a product a market cannot buy " +
      "are a dead page: /nl/nl/membership 404s and the NL FAQ's membership " +
      "tab was removed for that reason. DE is excluded for a second reason: " +
      "de-DE.json carries no legalHtml.membershipTerms__html, so serving the " +
      "route there throws next-intl MISSING_MESSAGE rather than rendering. " +
      "Add DE back the same commit that lands the German terms copy.",
  },
  "page:terms-conditions": {
    countries: ["ch", "nl", "de"],
    description:
      "General terms & conditions — CH, NL and DE. AT legal terms aren't " +
      "drafted yet; the route 404s rather than render Swiss terms. Add AT " +
      "back the same commit that lands the Austrian terms copy.",
  },
  "page:medical-data-sharing": {
    countries: ["ch", "nl", "de"],
    description:
      "Medical data sharing consent — CH, NL and DE. The DE booking flow's " +
      "consent box links /de/de/medical-data-sharing, so DE carries the CH " +
      "copy in de-DE.json/en-DE.json legalHtml with its links pointed at the " +
      "DE privacy/terms pages. AT's legal docs aren't drafted yet; the route " +
      "404s rather than render Swiss terms. Add AT back the same commit that " +
      "lands its legalHtml copy.",
  },

  "page:pricing": {
    countries: ["ch", "nl", "de", "at"],
    description:
      "Pricing page. AT was excluded in the 2026-08-28 launch-set trim because " +
      "there was no EUR price map for Austria and the page would have rendered " +
      "price-free. Austria got its own ladder on 2026-09-28 (core/advanced/pro " +
      "in FALLBACK_PRICES_EUR), so that reason expired and the page is back: a " +
      "waitlist site whose job is explaining the product and its prices needs " +
      "somewhere to put them, and the homepage package cards deep-link here. " +
      "Austria sells the three MRI tiers and nothing else, so the page's blood " +
      "section is gated off and its add-on sections self-hide on the ADDON_INFO " +
      "availability map. NL keeps the page but still hides it from the nav " +
      "pre-launch, which is a nav decision rather than this gate.",
  },
  "page:careers": {
    countries: ["ch", "nl", "de"],
    description:
      "Careers listing + job detail pages. Excluded from AT: no Austrian roles " +
      "to list while the market is pre-launch (2026-08-28 launch-set trim). " +
      "DE keeps the route but hides it from the footer.",
  },
  "page:authors": {
    countries: ["ch", "nl", "de"],
    description:
      "Author/reviewer bio pages. Excluded from AT (2026-08-28 launch-set " +
      "trim) — the only surface that links them is /insights/[slug], which AT " +
      "doesn't have either.",
  },
  "page:ambassadors": {
    countries: ["ch", "nl", "de"],
    description:
      "Ambassador programme landing page + its /applied thank-you page. " +
      "Excluded from AT (2026-08-28 launch-set trim); DE keeps the route but " +
      "hides it from the footer.",
  },

  "page:faq": {
    countries: ["ch", "nl", "de", "at"],
    description:
      "FAQ listing (plus its /[slug] and /category/[slug] children, which 404 " +
      "on their own once the CMS returns nothing). Excluded from AT in the " +
      "2026-08-28 launch-set trim — answering Swiss questions in an Austrian " +
      "market is worse than not answering yet — and reopened on 2026-09-28 so " +
      "answers can be released one at a time from the CMS. What makes that " +
      "safe is `de-AT` sitting in FALLBACK_SUPPRESSED_CMS_LOCALES: an " +
      "untranslated FAQ comes back empty, never Swiss, and `FaqSection` " +
      "renders nothing for an empty list. Note AT does NOT borrow de-DE here " +
      "the way it does for clinical copy — CMS_LOCALE_BORROWS_FROM is " +
      "deliberately clinical-only, because German FAQ answers talk about " +
      "statutory insurance Austria does not have. DE has its own " +
      "Vorsorge-Starter FAQ set in the de-DE CMS locale.",
  },
  "page:biomarkers": {
    countries: ["ch", "nl", "de", "at"],
    description:
      "Biomarker glossary listing (/biomarkers, served from v/[code]) and its " +
      "detail pages. Left AT in the 2026-08-28 launch-set trim as the largest " +
      "de-AT authoring debt; back on AT from 2026-10-05 once every de-AT " +
      "biomarker page had been reviewed (the detail pages were already live " +
      "and their breadcrumb linked this listing, a 404 until now).",
  },
  "page:tests/prostate-mri": {
    countries: ["ch", "nl"],
    description:
      "Prostate MRI single-test landing page. Excluded from AT (AHD-12966) " +
      "and from DE: neither market sells the scan — Germany sells the blood " +
      "check-up and its blood add-ons, nothing else. Prostate/pelvic MRI " +
      "have no CMS products row, so nav/carousel gating goes through this " +
      "registry rather than products.marketStatus.",
  },
  "page:tests/pelvic-mri": {
    countries: ["ch", "nl"],
    description:
      "Pelvic MRI single-test landing page. Excluded from AT (AHD-12966) and " +
      "from DE: same reason as page:tests/prostate-mri above.",
  },

  "page:tests/lpa": {
    countries: ["ch", "nl"],
    description:
      "Lp(a) single-biomarker landing page — CH-only (Nick's call in the " +
      "PSA/Lp(a) Eugene-review change, Aug 14: it is an add-on to the Swiss " +
      "Advanced blood test, and EUR markets have no authoritative price " +
      "for it). The route hard-guarded non-CH inline " +
      "from that day, but this entry was added for the 2026-08-28 trim " +
      "listing all four countries, so SSG, hreflang, nav and the locale " +
      "picker advertised /nl + /de + /at URLs that 404 — the weekly sweep's " +
      "`hreflang-resolves` findings. The entry now matches the route. " +
      "Opened to NL 2026-09-28 alongside its €29 NL price.",
  },
  "page:symptoms": {
    countries: ["ch", "nl", "de"],
    description:
      "Symptoms lens listing + detail. Already behind SYMPTOMS_ENABLED; the " +
      "country gate is additive so AT stays dark even if the flag goes on " +
      "(2026-08-28 launch-set trim).",
  },
  "page:teams": {
    countries: ["ch", "nl", "de"],
    description:
      "B2B teams page. Already behind the teams-page-enabled release flag; " +
      "the country gate is additive. Excluded from AT (2026-08-28 launch-set " +
      "trim) — the page renders hardcoded English strings rather than " +
      "next-intl, so it would show English on /at/de.",
  },
  "page:tools": {
    countries: ["ch", "de", "nl"],
    description:
      "The /tools index, listing the free health calculators. CH + DE + NL. " +
      "Its own closing CTA is a check-up, which all three markets sell or " +
      "waitlist, so the membership reasoning that kept the calculators " +
      "CH-only never applied to the index itself, and NL's lack of a " +
      "membership page does not either: this page never links to one. Ships " +
      "in all three Swiss locales, at /de/de/tools alone since DE offers " +
      "German only, and in both NL locales. Which locales actually have " +
      "something to list is derived from HEALTH_TOOLS (lib/health-tools.ts), " +
      "so the index cannot outlive its content in any market: on NL it is " +
      "empty of cards until at least one calculator's own gate opens there, " +
      "which is why this change is meant to land after them.",
  },
  "page:tools/bmi-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "BMI calculator SEO tool page. CH + DE + NL. The route's own LOCALES " +
      "holds de/en/fr/nl (the registry has no locale-level gate), so CH " +
      "prerenders de/en/fr, DE prerenders `de`, its only language, and NL " +
      "prerenders nl + en, the two it offers. DE joined once the German copy " +
      "landed in the de-DE catalog; its membership panel keeps the copy, " +
      "and isBookingOpen(country, 'membership') already turns that CTA into " +
      "the DE waitlist, the same coming-soon promise /de/de/membership " +
      "makes. NL needed one thing besides the Dutch and NL-English copy, and " +
      "it is that same panel: this market sells no membership at all " +
      "(page:membership, AHD-9361, 'no page, no promise'), so the panel's " +
      "own /membership link would hard-404 there. It now reads " +
      "page:membership rather than a country literal, and where that gate is " +
      "closed the panel points at the advanced blood test instead, which is " +
      "the product this market does sell. AT still out: trimmed to a " +
      "13-page waitlist site.",
  },
  "page:tools/bmr-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "BMR (basal metabolic rate) calculator SEO tool page. CH + DE + NL, on " +
      "the same terms as the BMI one: LOCALES holds de/en/fr/nl, so CH " +
      "prerenders de/en/fr, DE prerenders `de` alone, reading the German " +
      "copy in the de-DE catalog, and NL prerenders nl + en, reading the " +
      "nl and en-NL catalogs. Its membership panel " +
      "resolves to the DE waitlist in Germany; in NL there is no membership " +
      "to resolve to at all (page:membership, AHD-9361), so the panel reads " +
      "that gate and points at the advanced blood test instead. Mifflin-St " +
      "Jeor and Harris-Benedict carry no country term, so the rest of the " +
      "Dutch rollout was the copy. AT still out: trimmed to a 13-page " +
      "waitlist site.",
  },
  "page:tools/score2-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "SCORE2 cardiovascular-risk calculator SEO tool page. CH + DE, and it " +
      "took more than a translation to get there, unlike every other " +
      "calculator: SCORE2 is recalibrated per CVD risk region, Switzerland " +
      "is a low-risk region country and Germany a moderate one, so the gate " +
      "is paired with SCORE2_REGION_BY_COUNTRY (lib/score2.ts), which is " +
      "deliberately partial: a market with no entry there cannot be " +
      "computed at all. Germany's `moderate` entry landed in the same change " +
      "as this country list, and the difference is not cosmetic — the worked " +
      "example both pages print scores 6.1% on the Swiss calibration and " +
      "7.8% on the German one. The page also asks its two lipids in mg/dL " +
      "there, the unit German labs report " +
      "(feature:tools-mgdl-lipids, lib/lipid-units.ts). LOCALES holds " +
      "de/en/fr/nl, so CH prerenders de/en/fr, DE prerenders `de`, its only " +
      "language, and NL prerenders nl + en. The Netherlands needed the same " +
      "region entry Germany did, and it is the other answer: NL is LOW, like " +
      "Switzerland, so /nl/* scores the worked example at the same 6.1% the " +
      "Swiss pages print rather than Germany's 7.8%, and its copy names the " +
      "Netherlands rather than repeating Switzerland's claim. It did NOT " +
      "need the mg/dL entry: Dutch laboratories report lipids in mmol/L, so " +
      "feature:tools-mgdl-lipids stays DE-only. Its closing panel reads " +
      "page:membership, which NL does not have (AHD-9361), and points at the " +
      "advanced blood test instead. AT stays out: it is moderate like " +
      "Germany, but it is trimmed to a 13-page waitlist site, so its region " +
      "entry belongs in the change that gives it the page.",
  },
  "page:tools/fib4-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "FIB-4 liver fibrosis calculator SEO tool page. CH + DE, on the same " +
      "terms as the BMI and BMR ones rather than the SCORE2 one: FIB-4 is " +
      "four routine blood values and a square root, with no fitted " +
      "coefficients and no regional recalibration (lib/fib4.ts), so there " +
      "was nothing in the model a second market needed wired up and the " +
      "German copy in the de-DE catalog was the whole of the rollout. " +
      "LOCALES holds de/en/fr, so CH prerenders all three and DE prerenders " +
      "`de`, its only language. The German copy is not the Swiss one with " +
      "the orthography changed: German labs name the two enzymes AST and " +
      "ALT (GOT and GPT on older reports), where Swiss ones write ASAT and " +
      "ALAT, and they count platelets in Tsd/µl rather than G/L, which is " +
      "the same number under a different name. Its membership panel " +
      "resolves to the DE waitlist through isBookingOpen, and the check-up " +
      "hand-off in the result panel books the blood check-up directly. " +
      "NL joined on the same terms and with the same kind of " +
      "vocabulary difference: Dutch laboratories name the enzymes ASAT and " +
      "ALAT, as Swiss ones do, but count platelets in ×10⁹/l rather than " +
      "G/L, which is again the same number under a different name. NL has " +
      "the blood check-up too, so the hand-off renders there as well, but it has no " +
      "membership at all (page:membership, AHD-9361), so the closing " +
      "panel reads that gate and points at the advanced blood test instead " +
      "of a waitlist. AT still out: trimmed to a 13-page waitlist site.",
  },
  "page:tools/smaf5-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "sMAF-5 liver fibrosis risk calculator SEO tool page. CH + DE + NL, " +
      "on the same terms as the FIB-4 one rather than the SCORE2 one: " +
      "sMAF-5's six coefficients were fitted once, on US NHANES data, and " +
      "are applied unchanged in every country (lib/smaf5.ts), so there was " +
      "nothing in the model either later market needed wired up and the copy " +
      "was the whole of each rollout. LOCALES holds de/en/fr/nl, so CH " +
      "prerenders de/en/fr, DE prerenders `de`, its only language, and NL " +
      "prerenders nl + en. Same market vocabulary as the FIB-4 page in each " +
      "market: Germany reads AST rather than the Swiss ASAT and counts " +
      "platelets in Tsd/µl rather than G/L, while the Netherlands keeps ASAT " +
      "and writes platelets in ×10⁹/l. All of those are the same number " +
      "under a different name. Its membership panel resolves to the DE " +
      "waitlist through isBookingOpen; NL has no membership to resolve to at " +
      "all (page:membership, AHD-9361), so there the panel reads that gate " +
      "and points at the advanced blood test. The check-up hand-off in the " +
      "result panel books the blood check-up directly in every market. AT " +
      "still out: trimmed to a 13-page waitlist site.",
  },
  "page:tools/mafld-calculator": {
    countries: ["ch", "de", "nl"],
    description:
      "MAFLD criteria calculator SEO tool page. CH + DE + NL, on the same " +
      "terms as the FIB-4 and sMAF-5 ones rather than the SCORE2 one: the " +
      "MAFLD definition has no country term at all (lib/mafld.ts), so there " +
      "was nothing in the model either later market needed wired up. Its " +
      "one population caveat is ethnicity rather than market (the consensus " +
      "publishes lower BMI and waist cut-offs for Asian populations, and " +
      "this module implements the Caucasian set), which is the same caveat " +
      "in every country and is stated on the page. LOCALES holds " +
      "de/en/fr/nl, so CH prerenders de/en/fr, DE prerenders `de`, its only " +
      "language, and NL prerenders nl + en. Germany needed more than a " +
      "translation, unlike the other two liver pages, because this one " +
      "reads a lipid value and German labs report lipids in mg/dL: the " +
      "triglyceride field asks German readers in mg/dL and offers the " +
      "switch back to mmol/L (feature:tools-mgdl-lipids, " +
      "lib/lipid-units.ts). The Netherlands did NOT need that half: Dutch " +
      "laboratories report lipids in mmol/L already, the scale the model " +
      "computes in, so the field stays mmol/L there with no switch to " +
      "mis-set. What it needed instead was the closing panel, which reads " +
      "page:membership: NL sells no membership at all (AHD-9361), so there " +
      "the panel points at the advanced blood test. AT still out: trimmed " +
      "to a 13-page waitlist site.",
  },

  // --- Sections: block inside a shared page ---
  "section:insurance": {
    countries: ["ch", "de"],
    description:
      "Whole insurance/reimbursement section (heading + copy + coverage CTA), reused across homepage, pricing, etc. Hidden on NL — no reimbursement positioning there (AHD-6780).",
    // The `insurance` namespace is exclusively this section + the CH-only
    // /insurance page; nowhere un-gated renders it, so reviewers outside ch/de
    // can never see it.
    reviewKeyPrefixes: ["insurance."],
  },
  "section:home.hero-membership-pill": {
    countries: ["ch"],
    description:
      "'New: Ahead membership' pill above the homepage hero. CH-only — hidden on DE, and on NL while it launches as a waitlist (AHD-6898).",
  },
  "section:home.membership-banner": {
    countries: ["ch"],
    description:
      "Homepage 'Ahead membership' banner (HomepageMembershipSection — pill, heading, 'become a member' CTA, feature grid). CH-only — hidden on DE/AT (coming-soon EUR markets, membership not offered) and on NL, which launches blood-first without membership (AHD-6903).",
  },
  "section:go/bloodtest.membership": {
    countries: ["ch"],
    description:
      "Closing HomepageMembershipSection block on the /go/bloodtest ad LP. " +
      "Same component and same decision as section:home.membership-banner " +
      "(CH-only, hidden on DE/AT/NL) — the LP just rendered it un-gated, so " +
      "/de/de/go/bloodtest shipped a membership block promising " +
      "'Mitgliederpreise auf alle Check-ups' on a page whose add-ons show no " +
      "member price, plus a waitlist CTA competing with 'Bluttest buchen'. " +
      "/de/de/membership itself is unaffected (page:membership still ch+de).",
  },
  "section:go/bloodtest.hero-trust-strip": {
    countries: ["ch"],
    description:
      "Three-item trust strip under the hero CTA on the /go/bloodtest ad LP " +
      "(locations · reviewed-by-physicians · personal recommendations). " +
      "CH-only: on DE its first item was the Swiss location count and its " +
      "second is pricing.trustReviewedByPhysicians, which reads 'Von " +
      "Schweizer Ärztinnen und Ärzten geprüft' — a claim the German market " +
      "doesn't make, and one shared with the German pricing page so it can't " +
      "be reworded here alone. Pulled from DE until there is German copy for " +
      "it; the four-item strip lower on the page still carries the trust " +
      "message.",
  },
  "section:go/bloodtest.membership-comparison": {
    countries: ["ch"],
    description:
      "The 'membership vs one-off' pricing block on the /go/bloodtest ad LP " +
      "(BloodTrackingComparison). CH-only, because membership is only sold " +
      "there: on DE the comparison already dropped its membership card, " +
      "leaving one card under a subtitle that still offered to 'testen Sie " +
      "kontinuierlich mit unserer Mitgliedschaft'. DE renders the single " +
      "BloodTestCard instead, matching the German city LPs.",
  },
  "section:go/bloodtest.hero-cta2": {
    countries: ["de", "nl"],
    description:
      "Secondary hero CTA ('So funktioniert es' → /how-it-works) on the " +
      "/go/bloodtest ad LP, matching the German city LPs (go/bloodtest-munich " +
      "et al). DE-only because CH already fills that slot with 'Deckung " +
      "prüfen' (section:marketing.insurance-coverage-cta, CH-only) — running " +
      "both would give the Swiss hero three CTAs. NL (see " +
      "feature:go/bloodtest.waitlist-page) has the same empty slot as DE; AT " +
      "is a waitlist market where this page 404s. ⚠️ If the insurance CTA ever " +
      "opens to DE, drop this gate rather than shipping both.",
  },
  "section:go/bloodtest.blood-only": {
    countries: ["nl"],
    description:
      "Keeps the /go/bloodtest ad LP on the one product the market can book: " +
      "the secondary hero CTA goes to /quiz instead of the MRI-heavy " +
      "/how-it-works, and MRI testimonials (video tiles, quotes naming MRI) " +
      "drop out of the carousel. NL ad LPs are blood-only until MRI has a " +
      "launch date.",
  },
  "feature:go/bloodtest.waitlist-page": {
    countries: ["nl"],
    description:
      "Serves the /go/bloodtest ad LP in a market whose blood booking isn't " +
      "open yet, instead of 404ing. The page is public; its CTAs follow the " +
      "market's normal gating (waitlist modal until NL launches, booking after).",
  },
  "section:go/bloodtest.doctors-count": {
    countries: ["ch"],
    description:
      "The '30.000+ MRT-Befunde aus über 10 Jahren' stat in the expert block " +
      "of the /go/bloodtest ad LP (DoctorsSection `showCount`). CH-only: DE " +
      "does not sell the scan, so an MRI-volume credential on a blood-test " +
      "page proves the wrong product. Mirrors the static showCount={false} " +
      "the immunity LP already passes for the same reason.",
  },
  "section:home.swiss-insurer-logos": {
    countries: ["ch"],
    description: "Swiss insurer logo row in the homepage insurance section.",
  },
  "section:home.insurance-coverage-cta": {
    countries: ["ch"],
    description:
      "Insurance-section primary CTA points to /insurance (CH-only). Non-CH renders BookingCta instead.",
  },
  "section:marketing.insurance-coverage-cta": {
    countries: ["ch"],
    description:
      "Standalone insurance-coverage CTA block used on landing pages.",
  },
  "section:membership.locations-strip": {
    countries: ["ch"],
    description:
      "Swiss locations map on membership page — hidden on NL (AHD-6881).",
  },
  "section:contact.phone": {
    countries: ["ch"],
    description:
      "Prominent phone number on /contact (hero link + 'Call us' intent card, with a live-now dot during business hours). CH-only — it's a real staffed Swiss number (+41 44 797 69 46). NL has no staffed line, and DE/AT still ship placeholder contact data (AHD-8248). Originally removed site-wide by AHD-6829 (a NL-waitlist ticket that over-reached to CH); reinstated CH-scoped in AHD-10554.",
  },
  "section:report.licensed-practice": {
    countries: ["ch", "nl", "de"],
    description:
      "'Medizinische Fachpraxis' trust card on /report (and its markdown " +
      "mirror). Hidden in AT until Austria has its own licensed medical " +
      "practice; revisit then (AHD-14083).",
  },
  "section:contact.faq-cta": {
    countries: ["ch", "nl", "de"],
    description:
      "FAQ block and bottom 'first step' booking CTA on /contact (rendered page and markdown mirror). Hidden on AT until its booking, referral and price answers fit Austria; AHD-14109 tracks adding them back.",
  },
  "section:marketing.quiz-cta-banner": {
    countries: ["ch", "nl"],
    description:
      "The quiz CTA banner (headline + CTA + phone mockups) on pricing, test detail, and /go landing pages, plus the CMS landing-page block. CH + NL — opened to NL so the funnel has an in-site entry point instead of being direct-URL-only. ⚠️ Deliberate known trade-off (Willem, 2026-07-29): the banner's headline/CTA ARE translated (ui.quizCta*), but its phone mockups are hardcoded English and still depict the retired 3-step external quiz, so NL visitors see stale English artwork next to Dutch copy until AHD-9270 redraws them. Opened knowingly to get NL traffic into the funnel sooner; if that reads as too rough, revert this to [\"ch\"] — it's a one-line change. The exit-intent quiz card is covered separately by feature:exit-intent (still CH-only). Earlier revisions of this note also claimed the gate covers 'navbar mega-menu and mobile-menu quiz banners' — there is no quiz reference anywhere in components/ui/navbar/, so that was stale.",
    // No reviewKeyPrefixes. This used to claim `quiz.` was CH-exclusive, which
    // stopped being true when page:quiz opened to NL — keeping it would drop
    // every Dutch quiz string from the NL reviewer's queue, which is the exact
    // opposite of what a newly-translated namespace needs.
  },

  // --- Features: behaviour gates that aren't a visible section ---
  "section:nav.membership": {
    countries: ["ch"],
    description:
      "Membership in the navigation: the promo card on the right of the " +
      "desktop 'What we offer' mega menu, and the Membership link in the " +
      "mobile menu's account block. CH only. Germany sells the blood " +
      "check-up and its blood add-ons and nothing else, so the German nav " +
      "stopped advertising a coming-soon membership alongside it (the " +
      "/membership page itself stays — see page:membership). Waitlist NL " +
      "never offered membership at all (AHD-9361), and AT left " +
      'page:membership in 2026-08 — the mobile link\'s old `!== "nl"` check ' +
      "still pointed Austrian visitors at a page AT does not have.",
  },
  "feature:membership-first-positioning": {
    countries: ["ch"],
    description:
      "Membership-first positioning across the homepage (hero + USP chips + value props + services + how-it-works + pricing + closing CTA), the navbar (mega-menu membership card, no standalone Membership item, 'become a member' CTA), /pricing (membership tab leads), test detail hero prices (member rate leads), and the /how-it-works membership start line. Shipped from the homepage-membership-first-v1 experiment (AHD-6700). CH only — every other market keeps the testing-first copy: NL launches blood-first with no membership product (AHD-6910), and DE/AT are pre-launch EUR waitlist markets whose member pricing is CHF-only, so they advertise membership as coming-soon through page:membership instead of leading with it. AT was dropped from this gate (2026-08-28) because it had membership-led hero/nav/pricing copy with no AT membership product or EUR member price behind it — the same contradiction DE was always kept out of. Wider than a single section, so it's a feature gate; the mf* i18n keys are shared with the un-gated markets' fallbacks, so no reviewKeyPrefixes.",
  },
  "feature:membership-first-site": {
    countries: ["ch"],
    description:
      "Markets that get the membership-first site once RELEASE_MEMBERSHIP_FIRST is on (lib/membership-first.ts): booking links open the membership-first booking app, /membership forwards to the homepage, no top banner. The env flag says WHEN (Preview on, Production off until launch), this list says WHERE. CH only (Eugene, 2026-09-30). NL stays on the current booking flow; a market joins by adding its code here once the booking app sells its membership.",
  },
  "feature:waitlist-newsletter-optin": {
    countries: ["ch", "de", "nl"],
    description:
      "The optional newsletter checkbox on the waitlist's details step " +
      '("Ich möchte personalisierte Gesundheitstipps und Neuigkeiten …"). ' +
      "AT left on 2026-10-15 (Lydia, AHD-14148): the Austrian waitlist " +
      "collects no marketing consent at launch, so the form never asks for it " +
      "and always sends marketingConsent: false.",
  },
  "feature:exit-intent": {
    countries: ["ch"],
    description:
      "Exit-intent provider mounts only in CH (NL/DE don't have the funnel signal yet).",
  },
  "feature:nav-login": {
    countries: ["ch"],
    description:
      "Login link in navbar (desktop + mobile) — hidden in DE/NL until my.aheadhealth.com supports those markets.",
  },
  "feature:nav-search": {
    countries: ["ch"],
    description:
      "Desktop search icon in the navbar — CH only. Shipped to the base nav from the navbar-v4 test's search-icon win (v3 had dropped it globally); kept CH-scoped to match what was validated.",
  },
  "feature:chat": {
    countries: ["ch", "de", "nl", "at"],
    description:
      "The Ella chat — the global launcher (in the [locale] layout) + every " +
      "page-scoped chat surface (tests/biomarkers/what-we-detect/symptoms " +
      "detail `chatEligible`, and the AiAnswerLane mounts on fullbodymri / " +
      "insurance / search). An allow-list, not 'on wherever launched': chat " +
      "may only appear in a market the customer-support stack can actually " +
      "ground and staff. CH first; NL added at the 2026-09 Dutch go-live " +
      "(AHD-12443) — bilingual on both /nl/nl and /nl/en (the launcher keys " +
      "on country, so one entry covers both locales). DE added at the 2026-09 " +
      "German go-live (AHD-12325), after the cs side was confirmed live: " +
      "German market registry (EUR, 112 crisis line, no Swiss fallback), EUR " +
      "pricing with German payment methods (no TWINT leak), the draft-only " +
      "audience gate (LP_KB_AUDIENCE_GATE_ENABLED) so no agent-only policy " +
      "reaches chat, a DE KB, and German-market starter chips. Each market " +
      "clears the same bar; adding it here both mounts the real launcher for " +
      "everyone and retires that market's `ella-<mkt>` preview path (the " +
      "preview launcher self-gates off once this includes the country). AT " +
      "added at the 2026-10-16 Austrian site launch (AHD-14026), after the cs " +
      "side was live: AT_MARKET_ENABLED on cs-prod (Austrian crisis lines, " +
      "closed MRT-only catalogue, waitlist instead of booking), EUR prices " +
      "from the AT Stripe feed, and an AT KB. AiAnswerLane " +
      "surfaces still layer their own `aiAssistantEnabled()` env gate on top " +
      "via the `envEnabled` prop.",
  },
  "feature:tools-mgdl-lipids": {
    countries: ["de"],
    description:
      "Lipid entry in mg/dL on the calculator pages, rather than the mmol/L " +
      "the models compute in: the triglyceride field on " +
      "/tools/mafld-calculator and the two cholesterol fields on " +
      "/tools/score2-calculator. DE only, because this is a laboratory " +
      "convention rather than a preference — German labs report lipids in " +
      "mg/dL where Swiss ones report mmol/L, so in DE the mmol/L-only field " +
      "refused the correct number from the reader's own report and asked " +
      "them to convert, which is exactly the arithmetic that produces a " +
      "confidently wrong answer (159 typed into a term expecting 1.8 prints " +
      '"fat likely" over a clean liver). Where the gate allows a market, ' +
      "the field DEFAULTS to mg/dL and offers a switch to mmol/L; elsewhere " +
      "it is mmol/L with no switch, byte-identical to before. Both answers " +
      "come from lib/lipid-units.ts, which reads this gate, so adding a " +
      "market is this country list and nothing else. No reviewKeyPrefixes: " +
      "the unit copy lives in each calculator's own namespace beside " +
      "un-gated strings.",
  },
} as const satisfies Record<string, CountryGate>

export type GateId = keyof typeof COUNTRY_GATES

/** GateIds that refer to whole pages (route-level gates). */
export type PageGateId = Extract<GateId, `page:${string}`>

/** Is this gate's content allowed in the given country? */
export function isAllowedInCountry(id: GateId, country: Country): boolean {
  return (COUNTRY_GATES[id].countries as readonly string[]).includes(country)
}

/**
 * True when `key`'s copy belongs to a gate whose `reviewKeyPrefixes` claim it
 * and that gate is NOT allowed in `country` — i.e. a reviewer there can never
 * see the string in context. Used by the translation-review queue to drop
 * section/feature-gated copy; page gates are filtered separately by render path.
 */
export function isKeyCountryGatedOut(key: string, country: string): boolean {
  for (const gate of Object.values(COUNTRY_GATES) as CountryGate[]) {
    const prefixes = gate.reviewKeyPrefixes
    if (!prefixes) continue
    if (
      prefixes.some((p) => key.startsWith(p)) &&
      !(gate.countries as readonly string[]).includes(country)
    ) {
      return true
    }
  }
  return false
}

/**
 * Per-document market visibility — the data-driven companion to the static
 * registry above. CMS content collections (faq, blog-posts) carry an editor-set
 * `hideInCountries` deny-list; this returns true when the current market is on
 * it. Empty / absent list = visible everywhere. Where the registry gates whole
 * pages/sections, this gates individual CMS rows.
 */
export function isHiddenInCountry(
  doc: { hideInCountries?: readonly string[] | null },
  country: string
): boolean {
  return (
    Array.isArray(doc.hideInCountries) && doc.hideInCountries.includes(country)
  )
}

/**
 * Drop documents the editor hid in the current market (CMS `hideInCountries`).
 * A market hide removes the document everywhere — index, category, landing-page
 * embeds, and its own detail page. Lives here (not in `lib/cms-server.ts`, which
 * pulls in `next/cache` and can't be bundled into the client) so the explorer
 * and the server surfaces filter through one implementation.
 *
 * A biomarker's `packages` list is global, so `hideInCountries` is the only
 * per-market lever on the catalogue: NL stopped running four
 * hemolysis-sensitive markers (bilirubin, LDH, potassium, phosphate) that CH
 * and DE still run. The catalogue is fetched and cached **per locale**, but
 * hiding is **per country** — NL serves two locales (nl, en-NL) off one index —
 * so the filter cannot live in the fetcher. It belongs at each consumer, which
 * is the one place `country` is in scope.
 *
 * Every surface that counts, lists, links or prerenders biomarkers must run the
 * list through this: the explorer, `/biomarkers/v/[code]/[slug]` detail routes,
 * the "{n} biomarkers" counts, the sitemap, hreflang alternates, the search
 * index and llms.txt. Missing one doesn't break the page — it quietly
 * re-advertises a marker the market's lab does not run, which is why
 * `__tests__/biomarker-market-visibility.test.ts` pins the consumer list.
 */
export function visibleInCountry<
  T extends { hideInCountries?: readonly string[] | null },
>(docs: readonly T[], country?: string): T[] {
  // `country` is undefined for market-agnostic callers (the country-less
  // llms.txt; the search index builds its own per-country corpus), in which
  // case nothing is filtered.
  if (!country) return [...docs]
  return docs.filter((doc) => !isHiddenInCountry(doc, country))
}

/**
 * Country list for a `page:` gate. Use in `generateStaticParams`:
 *
 *   export function generateStaticParams() {
 *     return countriesForGatedPage("page:insurance").flatMap((country) =>
 *       getCountryConfigStatic(country).languages.map((locale) => ({
 *         country,
 *         locale,
 *       }))
 *     )
 *   }
 */
export function countriesForGatedPage(id: PageGateId): readonly Country[] {
  return COUNTRY_GATES[id].countries
}

/**
 * Map from a `META` page key (e.g. "insurance") to its `page:` gate ID.
 * Used by the search-index builder to translate page-keyed iteration
 * into gate lookups. Other surfaces (sitemap, hreflang) declare their
 * gate inline at each call site and don't need this map.
 *
 * Every `page:` entry in COUNTRY_GATES must appear as a value here. The
 * test in __tests__/country-gates.test.ts enforces parity.
 */
export const PAGE_KEY_TO_GATE_ID = {
  insurance: "page:insurance",
  membership: "page:membership",
  pricing: "page:pricing",
  careers: "page:careers",
  authors: "page:authors",
  ambassadors: "page:ambassadors",
  ambassadorsApplied: "page:ambassadors",
  "insurance-checker-terms": "page:insurance-checker-terms",
  "full-body-mri-zurich": "page:full-body-mri-zurich",
  "go/bloodtest-munich": "page:go/bloodtest-munich",
  "go/bloodtest-berlin": "page:go/bloodtest-berlin",
  "go/bloodtest-frankfurt": "page:go/bloodtest-frankfurt",
  "go/bloodtest-hamburg": "page:go/bloodtest-hamburg",
  "go/bloodtest-zurich": "page:go/bloodtest-zurich",
  "go/lauraphilipps": "page:go/lauraphilipps",
  "go/insurancemri": "page:go/insurancemri",
  "go/insuranceblood": "page:go/insuranceblood",
  "go/membership": "page:go/membership",
  "go/female-hormones": "page:go/female-hormones",
  "go/heart-health": "page:go/heart-health",
  "go/metabolism": "page:go/metabolism",
  "go/immunity": "page:go/immunity",
  quiz: "page:quiz",
  preventionSpecialistGpLanding:
    "page:careers/prevention-specialist-gp-landing",
  freeCall: "page:free-call",
  insights: "page:insights",
  compareHealthCheckUps: "page:compare-health-check-ups-switzerland",
  heartHealth: "page:heart-health",
  metabolism: "page:metabolism",
  femaleHormones: "page:female-hormones",
  cortisolBloodTest: "page:tests/cortisol-blood-test",
  psaBloodTest: "page:tests/psa-blood-test",
  membershipTerms: "page:membership-terms",
  terms: "page:terms-conditions",
  medicalDataSharing: "page:medical-data-sharing",
  faq: "page:faq",
  biomarkers: "page:biomarkers",
  lpaBloodTest: "page:tests/lpa",
  // Not META keys — these dynamic-route test pages have no meta() entry of
  // their own; they exist here only so the PAGE_KEY_TO_GATE_ID parity test
  // is satisfied (same as "full-body-mri-zurich" and the calculator pages).
  prostateMriTest: "page:tests/prostate-mri",
  pelvicMriTest: "page:tests/pelvic-mri",
  symptoms: "page:symptoms",
  teams: "page:teams",
  // Not META keys - the index and each calculator carry their own
  // title/description in their own namespace and none is in the search index,
  // so nothing iterating META reaches these entries. They exist because the
  // PAGE_KEY_TO_GATE_ID parity test requires every `page:` gate to be wired
  // here (same as "full-body-mri-zurich").
  tools: "page:tools",
  bmiCalculator: "page:tools/bmi-calculator",
  bmrCalculator: "page:tools/bmr-calculator",
  score2Calculator: "page:tools/score2-calculator",
  fib4Calculator: "page:tools/fib4-calculator",
  smaf5Calculator: "page:tools/smaf5-calculator",
  mafldCalculator: "page:tools/mafld-calculator",
} as const satisfies Record<string, PageGateId>

/**
 * Look up the page-gate (if any) for a given META page key. Returns
 * undefined for keys that aren't country-gated. Use this from any code
 * iterating META keys (e.g. the search-index builder); call sites with
 * a literal pageKey can index PAGE_KEY_TO_GATE_ID directly.
 */
export function getPageGateForKey(key: string): PageGateId | undefined {
  return (PAGE_KEY_TO_GATE_ID as Record<string, PageGateId | undefined>)[key]
}

/**
 * Map from URL path (without `/{country}/{locale}` prefix, leading slash
 * required) to the `page:` gate that governs it. Used by the locale
 * picker to decide whether to fall back to the country home when the
 * visitor switches to a country where the current page doesn't exist.
 */
export const PATH_TO_PAGE_GATE = {
  "/insurance": "page:insurance",
  "/membership": "page:membership",
  "/membership-terms": "page:membership-terms",
  "/terms-conditions": "page:terms-conditions",
  "/medical-data-sharing": "page:medical-data-sharing",
  "/pricing": "page:pricing",
  "/faq": "page:faq",
  "/biomarkers": "page:biomarkers",
  "/tests/lpa": "page:tests/lpa",
  "/tests/prostate-mri": "page:tests/prostate-mri",
  "/tests/pelvic-mri": "page:tests/pelvic-mri",
  "/symptoms": "page:symptoms",
  "/teams": "page:teams",
  "/careers": "page:careers",
  "/authors": "page:authors",
  "/ambassadors": "page:ambassadors",
  "/ambassadors/applied": "page:ambassadors",
  "/insurance-checker-terms": "page:insurance-checker-terms",
  "/full-body-mri-zurich": "page:full-body-mri-zurich",
  "/go/bloodtest-munich": "page:go/bloodtest-munich",
  "/go/bloodtest-berlin": "page:go/bloodtest-berlin",
  "/go/bloodtest-frankfurt": "page:go/bloodtest-frankfurt",
  "/go/bloodtest-hamburg": "page:go/bloodtest-hamburg",
  "/go/bloodtest-zurich": "page:go/bloodtest-zurich",
  "/go/lauraphilipps": "page:go/lauraphilipps",
  "/go/insurancemri": "page:go/insurancemri",
  "/go/insuranceblood": "page:go/insuranceblood",
  "/go/membership": "page:go/membership",
  "/go/female-hormones": "page:go/female-hormones",
  "/go/heart-health": "page:go/heart-health",
  "/go/metabolism": "page:go/metabolism",
  "/go/immunity": "page:go/immunity",
  "/quiz": "page:quiz",
  "/careers/prevention-specialist-gp-landing":
    "page:careers/prevention-specialist-gp-landing",
  "/free-call": "page:free-call",
  "/insights": "page:insights",
  "/compare-health-check-ups-switzerland":
    "page:compare-health-check-ups-switzerland",
  "/heart-health": "page:heart-health",
  "/metabolism": "page:metabolism",
  "/female-hormones": "page:female-hormones",
  "/tests/cortisol-blood-test": "page:tests/cortisol-blood-test",
  "/tests/psa-blood-test": "page:tests/psa-blood-test",
  "/tools": "page:tools",
  "/tools/bmi-calculator": "page:tools/bmi-calculator",
  "/tools/bmr-calculator": "page:tools/bmr-calculator",
  "/tools/score2-calculator": "page:tools/score2-calculator",
  "/tools/fib4-calculator": "page:tools/fib4-calculator",
  "/tools/smaf5-calculator": "page:tools/smaf5-calculator",
  "/tools/mafld-calculator": "page:tools/mafld-calculator",
} as const satisfies Record<string, PageGateId>

/**
 * Resolve which `page:` gate (if any) applies to a bare path. Returns
 * undefined for paths that aren't country-gated.
 */
export function gateForPath(path: string): PageGateId | undefined {
  // Normalise a translated URL (/versicherung) back to its English route
  // (/insurance) so a gate is keyed once, not once per locale.
  const canonical = canonicalSlugPath(path)
  return (PATH_TO_PAGE_GATE as Record<string, PageGateId | undefined>)[
    canonical
  ]
}

/**
 * Pages that render but are deliberately kept OUT of the index, by country.
 * `"all"` means every market.
 *
 * This is separate from a `page:` gate: a gated page doesn't exist in the
 * country at all, whereas these are live and linkable — they just must not be
 * submitted for indexing.
 *
 * The rule lives here because two surfaces need the same answer and used to
 * disagree: the page sets `robots: { index: false }`, and the sitemap has to
 * not list it. It listed all 7 anyway (AHD-10527). A noindexed URL in a
 * sitemap is a direct contradiction — we're asking Google to index a page that
 * tells it not to.
 */
export const NOINDEX_IN_COUNTRIES = {
  // Site search: a search-results page never belongs in an index, anywhere.
  "/search": "all",
} as const satisfies Record<string, "all" | readonly Country[]>

/**
 * Is a bare path noindexed in this country? Leading slash optional.
 */
export function isNoindexedInCountry(path: string, country: Country): boolean {
  const norm =
    path === "" || path === "/" ? "/" : `/${path.replace(/^\/+/, "")}`
  const rule = (
    NOINDEX_IN_COUNTRIES as Record<
      string,
      "all" | readonly Country[] | undefined
    >
  )[norm]
  if (!rule) return false
  return rule === "all" || rule.includes(country)
}

/**
 * Is a bare render path (locale-stripped, with or without a leading slash;
 * "" or "/" = home) reachable in a country? Ungated paths exist in every
 * country; gated paths follow their `page:` gate's country list. Used both to
 * drop unreachable strings from a reviewer's queue and to steer the cockpit's
 * jump to a page that actually exists in the locale under review.
 */
export function isPathAllowedInCountry(
  bare: string,
  country: Country
): boolean {
  const norm =
    bare === "" || bare === "/" ? "/" : `/${bare.replace(/^\/+/, "")}`
  const gate = gateForPath(norm)
  return gate ? isAllowedInCountry(gate, country) : true
}
